// Single owner of "is this tab still a live PRISM session?".
//
// PRISM must only ever be in one of two states: signed in and immediately
// usable, or on the login screen ready for a fresh sign-in. Anything in
// between — a chat page, admin panel, or open modal sitting on top of a dead
// cookie — is a state the clinician has to notice and fix with a manual
// refresh, so every path that learns the session is gone funnels through
// endSession() here rather than handling it locally.

const SESSION_FLAG_KEY = 'prism_session_active';
// The paired, NON-httpOnly half of the auth cookie pair. The backend issues it
// with set_auth_cookies() and drops it with clear_auth_cookies(), always in
// lockstep with the httpOnly prism_access cookie and with identical
// attributes — so its presence is the only client-readable evidence that a
// session cookie exists at all.
const CSRF_COOKIE_NAME = 'prism_csrf';
// Cross-tab channel. localStorage 'storage' events fire in every OTHER tab of
// the origin (never the writer), which is exactly the fan-out we want and is
// supported everywhere BroadcastChannel is not.
const SESSION_EVENT_KEY = 'prism_session_event';

// Guards against a second redirect racing the first — e.g. the axios
// interceptor and IdleTimeout's heartbeat both seeing the same 401.
let ending = false;

export function isSessionEnding() {
  return ending;
}

export function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export { CSRF_COOKIE_NAME };

// "Could this browser possibly have a session?" — evidence, never proof. A
// present cookie says nothing about whether the JWT inside it still verifies
// (expired, rotated secret, deactivated account); only a request the server
// answers settles that. An ABSENT cookie, though, is conclusive: there is no
// credential to send, so any authenticated call would 401 by definition.
export function hasCredentialCookie() {
  return readCookie(CSRF_COOKIE_NAME) !== null;
}

export function hasSession() {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(SESSION_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

// Per-tab hint only, used to avoid a login-screen flash on reload. The
// httpOnly prism_access cookie remains the real source of truth.
export function markSession(active) {
  if (typeof window === 'undefined') return;
  try {
    if (active) window.sessionStorage.setItem(SESSION_FLAG_KEY, '1');
    else window.sessionStorage.removeItem(SESSION_FLAG_KEY);
  } catch {
    /* storage disabled — the cookie still governs */
  }
}

function isOnLoginPage() {
  return typeof window !== 'undefined'
    && window.location.pathname.endsWith('/login');
}

// Tell the other tabs of this browser that the session flipped, so a tab left
// open in the background follows this one instead of going stale.
export function broadcastSessionEvent(type) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      SESSION_EVENT_KEY,
      JSON.stringify({ type, ts: Date.now() }),
    );
  } catch {
    /* storage disabled — cross-tab sync degrades, same-tab still works */
  }
}

// Subscribe to session events raised by other tabs. Returns an unsubscribe fn.
export function subscribeSessionEvents(handler) {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (event) => {
    if (event.key !== SESSION_EVENT_KEY || !event.newValue) return;
    let payload;
    try { payload = JSON.parse(event.newValue); } catch { return; }
    if (payload?.type) handler(payload.type);
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

// Terminate this tab's session view and land on /login. Always a full-page
// replace() rather than a router navigation: it drops every component's stale
// in-memory state (open modals, half-streamed answers, cached admin tables) so
// the login screen that comes up is genuinely ready to sign in again, and it
// leaves no history entry that the Back button could resurrect.
export function endSession({ broadcast = true } = {}) {
  if (ending) return;
  markSession(false);
  // Already on the sign-in screen: nothing to tear down, and crucially no
  // broadcast — a 401 from a public endpoint here (the manual link, help
  // feedback) must not kick signed-in siblings out of the app.
  if (typeof window === 'undefined' || isOnLoginPage()) return;
  ending = true;
  if (broadcast) broadcastSessionEvent('logout');
  window.location.replace('/login');
}

// Called after a successful sign-in so a sibling tab parked on /login moves to
// the app instead of showing a login form for a session that already exists.
export function notifySignedIn() {
  markSession(true);
  broadcastSessionEvent('login');
}
